import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TopicIssueRequestDto } from 'src/api/FiretrolApiClient';
import { meetingIssueApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export function useCreateMeetingIssue(jobId: number, meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (topicId: string) =>
      meetingIssueApiClient.createIssue(jobId, meetingId, topicId),
    onSettled: () =>
      invalidateFor('meetingIssue.save', qc, { jobId, meetingId }),
  });
}

/**
 * Issue edits autosave from debounced field changes, so there is no success
 * message — but a failure has to toast, or the edit silently disappears on the
 * next refetch. The scope makes TanStack run one write at a time per issue.
 */
export function useUpdateMeetingIssue(
  jobId: number,
  meetingId: string,
  topicId: string,
  issueId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `meeting-issue-${issueId}` },
    mutationFn: (request: TopicIssueRequestDto) =>
      meetingIssueApiClient.updateIssue(
        jobId,
        meetingId,
        topicId,
        issueId,
        request,
      ),
    meta: { errorContext: 'issue autosave' },
    onSettled: () =>
      invalidateFor('meetingIssue.save', qc, { jobId, meetingId }),
  });
}

export function useDeleteMeetingIssue(jobId: number, meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ topicId, issueId }: { topicId: string; issueId: string }) =>
      meetingIssueApiClient.deleteIssue(jobId, meetingId, topicId, issueId),
    meta: { successMessage: 'Issue deleted successfully' },
    onSettled: () =>
      invalidateFor('meetingIssue.save', qc, { jobId, meetingId }),
  });
}
